import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Save, RotateCcw, RefreshCw, Sparkles } from "lucide-react";
import { useApi, errMsg } from "../lib/api";
import { Button } from "./ui/button";

export const IntroEditor = ({ type, draft, setDraft, onApply }) => {
  const api = useApi();
  const [tpl, setTpl] = useState(null);
  const [ask, setAsk] = useState("");
  const [thinking, setThinking] = useState(false);
  useEffect(() => {
    let live = true;
    api.get(`/email-preview/templates/${type}`).then((r) => { if (live) { setTpl(r.data); setDraft(r.data.intro); } }).catch((e) => toast.error(errMsg(e)));
    return () => { live = false; };
  }, [api, type, setDraft]);
  if (!tpl) return null;
  const save = async () => {
    try { const r = await api.put(`/email-preview/templates/${type}`, { intro: draft }); setTpl(r.data); onApply(null); toast.success("Opening text saved. Future reminders will use it."); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const reset = async () => {
    try { const r = await api.delete(`/email-preview/templates/${type}`); setTpl(r.data); setDraft(r.data.intro); onApply(null); toast.success("Back to the standard wording"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const dirty = draft !== tpl.intro;
  const suggest = async () => {
    setThinking(true);
    try { const r = await api.post(`/email-preview/templates/${type}/suggest`, { instructions: ask, current: draft }); setDraft(r.data.intro); onApply(r.data.intro); toast.success("Suggestion ready. Review it, then Save to use it."); }
    catch (e) { toast.error(errMsg(e)); }
    setThinking(false);
  };
  return (
    <div className="grid gap-2 border-t border-[#1F2A3E] pt-4" data-testid="intro-editor">
      <div className="flex items-center justify-between"><span className="text-xs text-slate-400">Opening text</span>
        <span className="text-[11px] text-slate-500" data-testid="intro-editor-status">{tpl.custom ? "Custom wording" : "Standard wording"}</span></div>
      <textarea className="c-input min-h-[150px] text-sm leading-relaxed" value={draft} maxLength={1500} onChange={(e) => setDraft(e.target.value)} data-testid="intro-editor-textarea" />
      <p className="text-[11px] text-slate-500">Use {"{contractor}"}, {"{challan}"}, {"{month}"} and {"{due}"}; they are filled in and shown in bold. The greeting and closing line stay the same.</p>
      <div className="flex gap-2">
        <input className="c-input text-xs" placeholder="Ask AI, e.g. 'friendlier' or 'mention the 16th hold'" value={ask} maxLength={500} onChange={(e) => setAsk(e.target.value)} data-testid="intro-editor-ask-input" />
        <Button size="sm" variant="outline" className="border-[#00E599]/30 text-[#00E599] hover:bg-[#00E599]/10 shrink-0" disabled={thinking} onClick={suggest} data-testid="intro-editor-suggest-btn">
          <Sparkles size={13} className="mr-1" />{thinking ? "Writing…" : "Suggest"}</Button>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" className="border-[#1F2A3E] hover:bg-[#1A2234]" disabled={!dirty} onClick={() => onApply(draft)} data-testid="intro-editor-preview-btn"><RefreshCw size={13} className="mr-1" />Preview</Button>
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 text-white" disabled={!dirty || draft.trim().length < 10} onClick={save} data-testid="intro-editor-save-btn"><Save size={13} className="mr-1" />Save</Button>
        {tpl.custom && <Button size="sm" variant="ghost" className="text-slate-400 hover:text-white" onClick={reset} data-testid="intro-editor-reset-btn"><RotateCcw size={13} className="mr-1" />Reset</Button>}
      </div>
    </div>);
};
